import { useEtudeBreadcrumbItems } from "components/EtudeForm/hooks/useEtudeBreadcrumb";
import { Page } from "components/Page";
import { Popup } from "components/popups/Popup";
import Button from "components/shared/button";
import Loader from "components/shared/loader";
import { Text } from "components/shared/text";
import { usePopup } from "providers/PopupProvider";
import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Navigate,
  Outlet,
  useLocation,
  useNavigate,
  useParams,
} from "react-router";
import { Link } from "react-router";
import { APP_URL, EtudeStep, getAppUrl, ObsolescenceType } from "utils/defines";
import { useSalesNetwork } from "utils/hooks/useSalesNetworks";
import useTranslation from "utils/hooks/useTranslation";
import { EtudeTypes } from "./constants";
import { useCurrentEtudeData } from "./hooks/useCurrentEtudeData";
import { useMatchEtudePath } from "./hooks/useMatchEtudePath";
import { useSetCurrentEtudeData } from "./hooks/useSetCurrentEtudeData";
import { useAppSelector } from "store/hooks";
import { ServiceNames } from "./Solutions/Solutions";

const enableObsolescence = true;
export const EtudeLayout: React.FC = () => {
  const { network } = useSalesNetwork();
  const { etudeType } = useParams();
  const { isEtudeFetching } = useSetCurrentEtudeData();
  const { breadcrumbItems, title } = useEtudeBreadcrumbItems();
  const { solution, json, service, isLoading } = useCurrentEtudeData();
  const { matchEtudePath } = useMatchEtudePath();
  const solutions = useAppSelector((state) => state.etude.solutions);
  const [obsolescenceType, setObsolescenceType] =
    useState<ObsolescenceType | null>(null);
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const { openPopup, closePopup } = usePopup();

  const popupKey = useRef<string | null>(null);

  const obsoletAccesssoireCodes = useMemo(() => {
    if (!solution || !json?.selectedSolution) return [];

    const obsolescenceAccessoiresSupp = Object.entries(
      json?.accessoires?.selectedAccessoiresSupp ?? {},
    ).filter(
      ([code, value]) =>
        !!value &&
        !solution?.accessoires.some(
          (a) => a.code === code && a.tag == "SUPP" && a.thercodim_active,
        ),
    );
    const obsolescenceAccessoiresNonSupp = Object.entries(
      json?.accessoires?.selectedAccessoiresNonSupp ?? {},
    ).filter(
      ([code, value]) =>
        !!value &&
        !solution?.accessoires.some(
          (a) => a.code === code && a.tag !== "SUPP" && a.thercodim_active,
        ),
    );
    const obsolescenceBoilersCodes = Object.entries(
      json?.accessoires?.selectedBoilers ?? {},
    ).filter(
      ([code, value]) =>
        !!value &&
        (solution?.chaudiere?.code != code ||
          !solution?.chaudiere?.thercodim_active),
    );
    const obsolescenceServicesCodes = Object.entries(
      json?.accessoires?.selectedServices ?? {},
    ).filter(
      ([code, value]) =>
        !!value &&
        !solution?.services.some((s) => s.code === code && s.thercodim_active),
    );

    return [
      ...obsolescenceAccessoiresSupp,
      ...obsolescenceAccessoiresNonSupp,
      ...obsolescenceBoilersCodes,
      ...obsolescenceServicesCodes,
    ].map(([code]) => {
      const item =
        solution?.accessoires.find((a) => a.code === code) ||
        solution?.services.find((s) => s.code === code) ||
        (solution?.chaudiere?.code === code ? solution.chaudiere : null);

      return { code, nom: item?.nom || code };
    });
  }, [json, solution]);

  const obsoleteECS = useMemo(() => {
    if (!solution || !json?.selectedSolution) return [];

    const selectedEchangeur = json.selectedSolution.ecs?.echangeur;
    const freshEchangeur = solution.ecs?.echangeur;
    const isEchangeurObsolete =
      selectedEchangeur && freshEchangeur && !freshEchangeur.thercodim_active;
    const obsoleteEchangeurList = isEchangeurObsolete ? [freshEchangeur] : [];

    const selectedBallons =
      json.selectedSolution.ecs?.ballons_stockage_ecs ?? [];
    const freshBallons = solution.ecs?.ballons_stockage_ecs ?? [];

    const obsoleteBallons = selectedBallons.filter((sb: any) => {
      const fb = freshBallons.find((f: any) => f.code === sb.code);
      return fb && !fb.thercodim_active;
    });

    return [...obsoleteEchangeurList, ...obsoleteBallons];
  }, [solution, json]);

  const obsoleteChauffage = useMemo(() => {
    if (!solution || !json?.selectedSolution) return [];
    if (service === ServiceNames.ECS) return [];

    let obsoleteBallons: any[] = [];
    const selectedBallonsRaw = json.selectedSolution.ballons;
    const freshBallonsRaw = solution.ballons;

    if (Array.isArray(selectedBallonsRaw)) {
      const freshList = Array.isArray(freshBallonsRaw) ? freshBallonsRaw : [];
      obsoleteBallons = selectedBallonsRaw.filter((sb: any) => {
        const fb = freshList.find((f: any) => f.code === sb.code);

        if (fb?.code == "FXBALLON") return;
        return fb && !fb.thercodim_active;
      });
    } else if (selectedBallonsRaw) {
      const selectedElec = selectedBallonsRaw as any;
      const freshElec = freshBallonsRaw as any;
      if (freshElec) {
        const obsoletePrimaires = (selectedElec.primaires ?? []).filter(
          (sb: any) => {
            const fb = freshElec.primaires?.find((f: any) => f.code === sb.code);

            if (fb?.code == "FXBALLON") return;

            return fb && !fb.thercodim_active;
          },
        );
        const obsoleteSecondaires = (selectedElec.secondaires ?? []).filter(
          (sb: any) => {
            const fb = freshElec.secondaires?.find(
              (f: any) => f.code === sb.code,
            );

            if (fb?.code == "FXBALLON") return;
            return fb && !fb.thercodim_active;
          },
        );
        obsoleteBallons = [...obsoletePrimaires, ...obsoleteSecondaires];
      }
    }

    return obsoleteBallons;
  }, [solution, json, service]);


  useEffect(() => {

    if (enableObsolescence && solutions.length > 0 && !isLoading) {
      if (
        solution &&
        matchEtudePath(EtudeStep.SolutionDetails) &&
        !solution.pac.thercodim_active
      ) {
        setObsolescenceType(ObsolescenceType.PAC);
      } else if (
        matchEtudePath(EtudeStep.Documents) &&
        obsoletAccesssoireCodes.length > 0
      ) {
        setObsolescenceType(ObsolescenceType.Accessoires);
      } else if (obsoleteECS.length > 0) {
        setObsolescenceType(ObsolescenceType.ECS);
      } else if (obsoleteChauffage.length > 0) {
        setObsolescenceType(ObsolescenceType.CHAUFFAGE);
      } else {
        setObsolescenceType(null);
      }
    }
  }, [solution, location.pathname, solutions, obsoleteECS, obsoleteChauffage, isLoading]);

  useEffect(() => {
    if (network) {
      if (
        !network.services
          .map((s: any) => s.code)
          .includes(etudeType as string)
      ) {
        navigate(getAppUrl("/", { network: network?.code }));
      }
    }
  }, [network, etudeType]);

  if (!Object.values(EtudeTypes).includes(etudeType!)) {
    return <Navigate to={getAppUrl("/", { network: network?.code })} />;
  }

  useEffect(() => {
    if (obsolescenceType !== null && !popupKey.current) {
      const currentPopupKey = openPopup(
        <Popup portal title={t("FO_STUDIES_NOT_AVAILABLE")} allowClose={false}>
          <group
            data-length="450"
            data-direction="column"
            data-gap="10"
            data-space="10"
            data-align="start"
            data-justify="start"
          >
            <Text data-space="5">
              {t("FO_STUDIES_NOT_AVAILABLE")}
              <br />
              {obsolescenceType === ObsolescenceType.PAC && solution?.pac && (
                <Text>{solution?.pac.nom}</Text>
              )}
              {obsolescenceType === ObsolescenceType.Accessoires && (
                <group data-direction="column" data-gap="10">
                  {obsoletAccesssoireCodes.map(({ code, nom }) => (
                    <Text key={code}>{nom as string}</Text>
                  ))}
                </group>
              )}
              {obsolescenceType === ObsolescenceType.ECS && (
                <group data-direction="column" data-gap="10">
                  {obsoleteECS.map((item) => (
                    <Text key={item.code}>{item.nom}</Text>
                  ))}
                </group>
              )}
              {obsolescenceType === ObsolescenceType.CHAUFFAGE && (
                <group data-direction="column" data-gap="10">
                  {obsoleteChauffage.map((item: any) => (
                    <Text key={item.code}>{item.nom}</Text>
                  ))}
                </group>
              )}
              <br />
              {t("FO_STUDIES_UPDATE")}
            </Text>
            <group
              data-gap="5"
              data-wrap="no"
              data-length="autofit"
              data-justify="center"
            >
              <Link
                to={
                  breadcrumbItems.find((item) => {
                    switch (obsolescenceType) {
                      case ObsolescenceType.PAC:
                        return item.step === EtudeStep.Solutions;
                      case ObsolescenceType.ECS:
                        return item.step === EtudeStep.InputForm;
                      case ObsolescenceType.CHAUFFAGE:
                        return item.step === EtudeStep.InputForm;
                      case ObsolescenceType.Accessoires:
                        return item.step === EtudeStep.Accessoires;
                      default:
                        return false;
                    }
                  })?.link ?? getAppUrl(`/etude/${APP_URL.MyEtudes}`)
                }
              >
                <Button primary>{t("valider")}</Button>
              </Link>
              <Link to={getAppUrl(`/${APP_URL.MyEtudes}`)}>
                <Button primary>{t("cancel")}</Button>
              </Link>
            </group>
          </group>
        </Popup >,
      );
      popupKey.current = currentPopupKey || null;
    }
    return () => {
      if (popupKey.current) {
        closePopup(popupKey.current);
        popupKey.current = null;
      }
    };
  }, [obsolescenceType, solution]);

  return (
    <Page title={title} breadCrumb={breadcrumbItems}>
      {isEtudeFetching && <Loader />}

      {!obsolescenceType && <Outlet />}
    </Page>
  );
};
